import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import {
  Plus, X, Trash2, Users, FileText, DollarSign, ArrowLeft, Loader2,
  Send, Ban, CheckCircle2, Mail, Phone, Search, ChevronRight, Edit3, Download, FileDown,
} from "lucide-react";
import SendDocModal from "@/components/bo/SendDocModal";
import StatementModal from "@/components/bo/StatementModal";
import BulkSendModal from "@/components/bo/BulkSendModal";

const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS_PILLS = {
  draft:     { en: "Draft",     es: "Borrador",   cls: "bg-white border-[#737373] text-[#737373]" },
  sent:      { en: "Sent",      es: "Enviada",    cls: "bg-white border-[#C4A252] text-[#8B1C22]" },
  partial:   { en: "Partial",   es: "Parcial",    cls: "bg-[#FDFBF7] border-[#C4A252] text-[#C4A252]" },
  paid:      { en: "Paid",      es: "Pagada",     cls: "bg-white border-[#15803D] text-[#15803D]" },
  overdue:   { en: "Overdue",   es: "Vencida",    cls: "bg-white border-[#8B1C22] text-[#8B1C22]" },
  cancelled: { en: "Cancelled", es: "Cancelada",  cls: "bg-white border-[#737373] text-[#737373] line-through" },
};

const TERMS = [
  { key: "due_on_receipt", en: "Due on Receipt", es: "Pago Inmediato" },
  { key: "net_7", en: "Net 7", es: "Neto 7" },
  { key: "net_15", en: "Net 15", es: "Neto 15" },
  { key: "net_30", en: "Net 30", es: "Neto 30" },
  { key: "net_60", en: "Net 60", es: "Neto 60" },
];

const Modal = ({ open, onClose, title, children, testid, wide = false }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={testid}>
      <div className={`bg-white ${wide ? "max-w-3xl" : "max-w-lg"} w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl">{title}</h3>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

/* Customer form */
const CustomerForm = ({ bookId, lang, existing, onCancel, onSaved }) => {
  const [form, setForm] = useState(existing || {
    name: "", company: "", email: "", phone: "", billing_address: "", default_terms: "net_30", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (existing) {
        const { data } = await api.patch(`/back-office/books/${bookId}/customers/${existing.id}`, form);
        toast.success("Customer updated"); onSaved(data);
      } else {
        const { data } = await api.post(`/back-office/books/${bookId}/customers`, form);
        toast.success("Customer added"); onSaved(data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="customer-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Nombre" : "Name"}</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rn3-input" data-testid="cf-name" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Empresa" : "Company"}</label>
          <input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rn3-input" data-testid="cf-company" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">Email</label>
          <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rn3-input" data-testid="cf-email" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Teléfono" : "Phone"}</label>
          <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rn3-input" data-testid="cf-phone" />
        </div>
      </div>
      <div>
        <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Dirección de Facturación" : "Billing Address"}</label>
        <textarea value={form.billing_address || ""} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className="rn3-input" rows="2" data-testid="cf-addr" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Términos Predeterminados" : "Default Terms"}</label>
          <select value={form.default_terms} onChange={(e) => setForm({ ...form, default_terms: e.target.value })} className="rn3-input" data-testid="cf-terms">
            {TERMS.map((t) => <option key={t.key} value={t.key}>{lang === "es" ? t.es : t.en}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Notas" : "Notes"}</label>
        <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rn3-input" rows="2" data-testid="cf-notes" />
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="rn3-btn-gold">Cancel</button>
        <button disabled={busy} className="rn3-btn-primary" data-testid="cf-submit">{busy ? "…" : (existing ? "Save" : "Add Customer")}</button>
      </div>
    </form>
  );
};

/* Invoice form (create / edit) — set mode="estimate" to POST to /estimates */
const InvoiceForm = ({ bookId, customers, incomeAccounts, lang, existing, mode = "invoice", onCancel, onSaved }) => {
  const [form, setForm] = useState(existing || {
    customer_id: customers[0]?.id || "",
    date: todayISO(),
    terms: "net_30",
    items: [{ description: "", quantity: 1, unit_price: 0, account_code: incomeAccounts[0]?.code || "4010" }],
    tax_rate: 0,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const totals = useMemo(() => {
    const sub = (form.items || []).reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.unit_price || 0)), 0);
    const tax = sub * Number(form.tax_rate || 0) / 100;
    return { subtotal: sub, tax, total: sub + tax };
  }, [form]);

  const updateItem = (i, key, val) => {
    const items = [...form.items];
    items[i] = { ...items[i], [key]: val };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0, account_code: incomeAccounts[0]?.code || "4010" }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "estimate") {
        const { data } = await api.post(`/back-office/books/${bookId}/estimates`, {
          customer_id: form.customer_id, date: form.date, items: form.items,
          tax_rate: Number(form.tax_rate || 0), notes: form.notes,
        });
        toast.success(`Created ${data.estimate_number}`); onSaved(data);
      } else if (existing) {
        const { data } = await api.patch(`/back-office/books/${bookId}/invoices/${existing.id}`, {
          customer_id: form.customer_id, date: form.date, terms: form.terms, items: form.items, tax_rate: Number(form.tax_rate || 0), notes: form.notes,
        });
        toast.success("Invoice updated"); onSaved(data);
      } else {
        const { data } = await api.post(`/back-office/books/${bookId}/invoices`, { ...form, tax_rate: Number(form.tax_rate || 0) });
        toast.success(`Created ${data.invoice_number}`); onSaved(data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="invoice-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Cliente" : "Customer"}</label>
          <select required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className="rn3-input" data-testid="if-customer">
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Términos" : "Terms"}</label>
          <select value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="rn3-input" data-testid="if-terms">
            {TERMS.map((t) => <option key={t.key} value={t.key}>{lang === "es" ? t.es : t.en}</option>)}
          </select>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Fecha" : "Date"}</label>
          <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rn3-input" data-testid="if-date" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Tasa de Impuesto (%)" : "Tax Rate (%)"}</label>
          <input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className="rn3-input" data-testid="if-tax" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="rn3-eyebrow">{lang === "es" ? "Líneas" : "Line Items"}</div>
          <button type="button" onClick={addItem} className="text-xs text-[#8B1C22] inline-flex items-center gap-1" data-testid="if-add-item"><Plus className="w-3 h-3" /> {lang === "es" ? "Línea" : "Add Line"}</button>
        </div>
        <div className="space-y-2">
          {form.items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`if-item-${i}`}>
              <input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} placeholder={lang === "es" ? "Descripción" : "Description"} className="col-span-5 rn3-input text-sm" data-testid={`if-item-desc-${i}`} required />
              <input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} placeholder="Qty" className="col-span-1 rn3-input text-sm" data-testid={`if-item-qty-${i}`} />
              <input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} placeholder="Price" className="col-span-2 rn3-input text-sm" data-testid={`if-item-price-${i}`} />
              <select value={it.account_code} onChange={(e) => updateItem(i, "account_code", e.target.value)} className="col-span-3 rn3-input text-sm" data-testid={`if-item-acc-${i}`}>
                {incomeAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} {(lang === "es" && a.name_es) || a.name}</option>)}
              </select>
              <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-[#737373] hover:text-[#8B1C22]" data-testid={`if-item-rm-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-[#E5E5E5] pt-4 grid grid-cols-2 gap-4">
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Notas" : "Notes"}</label>
          <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rn3-input" rows="2" data-testid="if-notes" />
        </div>
        <div className="text-right space-y-1 text-sm">
          <div className="flex justify-between"><span>{lang === "es" ? "Subtotal" : "Subtotal"}</span><span className="tabular-nums">{fmt(totals.subtotal)}</span></div>
          <div className="flex justify-between text-[#737373]"><span>{lang === "es" ? "Impuesto" : "Tax"} ({form.tax_rate}%)</span><span className="tabular-nums">{fmt(totals.tax)}</span></div>
          <div className="flex justify-between font-serif-display text-2xl pt-2 border-t border-[#C4A252]"><span>{lang === "es" ? "Total" : "Total"}</span><span className="tabular-nums">{fmt(totals.total)}</span></div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="rn3-btn-gold">Cancel</button>
        <button disabled={busy} className="rn3-btn-primary" data-testid="if-submit">{busy ? "…" : (existing ? (lang === "es" ? "Guardar" : "Save Changes") : (lang === "es" ? "Crear Factura" : "Create Invoice"))}</button>
      </div>
    </form>
  );
};

/* Payment form */
const PaymentForm = ({ bookId, invoice, accounts, lang, onCancel, onSaved }) => {
  const bankAccounts = accounts.filter((a) => !a.is_header && a.is_active && (a.subtype === "bank" || a.code.startsWith("10")));
  const [form, setForm] = useState({
    date: todayISO(),
    amount: invoice.balance || invoice.total,
    cash_account_code: bankAccounts[0]?.code || "1010",
    method: "check",
    reference: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/back-office/books/${bookId}/invoices/${invoice.id}/payments`, { ...form, amount: Number(form.amount) });
      toast.success(lang === "es" ? "Pago registrado" : "Payment recorded");
      onSaved(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="payment-form">
      <div className="bg-[#FDFBF7] border border-[#C4A252] p-3 text-sm">
        <div>{invoice.invoice_number} · {invoice.customer_name}</div>
        <div className="font-serif-display text-2xl mt-1">{lang === "es" ? "Saldo" : "Balance"}: {fmt(invoice.balance)}</div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Fecha" : "Date"}</label>
          <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rn3-input" data-testid="pf-date" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Monto" : "Amount"}</label>
          <input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rn3-input" data-testid="pf-amount" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Cuenta de Banco" : "Bank Account"}</label>
          <select value={form.cash_account_code} onChange={(e) => setForm({ ...form, cash_account_code: e.target.value })} className="rn3-input" data-testid="pf-bank">
            {bankAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Método" : "Method"}</label>
          <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="rn3-input" data-testid="pf-method">
            {["check","cash","ach","card","wire","other"].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Referencia" : "Reference / Check #"}</label>
          <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="rn3-input" data-testid="pf-ref" />
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="rn3-btn-gold">Cancel</button>
        <button disabled={busy} className="rn3-btn-primary" data-testid="pf-submit">{busy ? "…" : (lang === "es" ? "Registrar Pago" : "Record Payment")}</button>
      </div>
    </form>
  );
};

/* Invoice detail view */
const InvoiceDetail = ({ bookId, invoiceId, accounts, lang, onBack, onChanged }) => {
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPay, setShowPay] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [customers, setCustomers] = useState([]);
  const incomeAccounts = useMemo(() => accounts.filter((a) => a.type === "income" && !a.is_header && a.is_active), [accounts]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: i }, { data: cs }] = await Promise.all([
        api.get(`/back-office/books/${bookId}/invoices/${invoiceId}`),
        api.get(`/back-office/books/${bookId}/customers`),
      ]);
      setInv(i); setCustomers(cs);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [invoiceId]);

  const setStatus = async (status) => {
    try {
      const { data } = await api.patch(`/back-office/books/${bookId}/invoices/${invoiceId}`, { status });
      setInv((p) => ({ ...p, ...data }));
      onChanged?.();
    } catch (err) { toast.error("Failed"); }
  };

  const removeInvoice = async () => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/back-office/books/${bookId}/invoices/${invoiceId}`);
      toast.success("Deleted");
      onBack();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const voidPayment = async (paymentId) => {
    if (!window.confirm("Void this payment?")) return;
    await api.delete(`/back-office/books/${bookId}/invoices/${invoiceId}/payments/${paymentId}`);
    toast.success("Payment voided"); load(); onChanged?.();
  };

  if (loading || !inv) return <div className="py-12 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;
  const accName = (code) => accounts.find((a) => a.code === code);
  const c = inv.customer || {};
  const pill = STATUS_PILLS[inv.status] || STATUS_PILLS.draft;

  return (
    <div className="space-y-6" data-testid="invoice-detail">
      <button onClick={onBack} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid="inv-back">
        <ArrowLeft className="w-3 h-3" /> {lang === "es" ? "Volver" : "Back to Invoices"}
      </button>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 rn3-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="rn3-eyebrow flex items-center gap-2">
                {inv.invoice_number}
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${pill.cls}`}>{lang === "es" ? pill.es : pill.en}</span>
              </div>
              <h2 className="font-serif-display text-3xl mt-2">{c.name}</h2>
              {c.company && <div className="text-sm text-[#737373]">{c.company}</div>}
              <div className="text-xs text-[#737373] mt-1 space-y-0.5">
                {c.email && <div className="inline-flex items-center gap-1 mr-3"><Mail className="w-3 h-3" /> {c.email}</div>}
                {c.phone && <div className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</div>}
              </div>
            </div>
            <div className="text-right text-sm">
              <div>{lang === "es" ? "Fecha" : "Date"}: {inv.date}</div>
              <div>{lang === "es" ? "Vence" : "Due"}: <b>{inv.due_date}</b></div>
              <div className="text-xs text-[#737373] mt-1">{TERMS.find(t=>t.key===inv.terms)?.[lang]||inv.terms}</div>
            </div>
          </div>

          <table className="w-full text-sm mt-6">
            <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5]">
              <tr><th className="py-2">{lang === "es" ? "Descripción" : "Description"}</th><th className="py-2 text-right w-16">Qty</th><th className="py-2 text-right w-24">{lang === "es" ? "Precio" : "Price"}</th><th className="py-2 text-right w-24">{lang === "es" ? "Total" : "Total"}</th></tr>
            </thead>
            <tbody>
              {(inv.items || []).map((it, i) => (
                <tr key={i} className="border-b border-[#F3EFE9]">
                  <td className="py-2">
                    <div>{it.description}</div>
                    <div className="text-xs text-[#737373]">{accName(it.account_code)?.code} — {(lang === "es" && accName(it.account_code)?.name_es) || accName(it.account_code)?.name}</div>
                  </td>
                  <td className="py-2 text-right tabular-nums">{it.quantity}</td>
                  <td className="py-2 text-right tabular-nums">{fmt(it.unit_price)}</td>
                  <td className="py-2 text-right tabular-nums">{fmt(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 text-sm space-y-1 max-w-xs ml-auto">
            <div className="flex justify-between"><span>{lang === "es" ? "Subtotal" : "Subtotal"}</span><span className="tabular-nums">{fmt(inv.subtotal)}</span></div>
            <div className="flex justify-between text-[#737373]"><span>{lang === "es" ? "Impuesto" : "Tax"} ({inv.tax_rate}%)</span><span className="tabular-nums">{fmt(inv.tax_amount)}</span></div>
            <div className="flex justify-between font-serif-display text-2xl border-t border-[#C4A252] pt-2"><span>{lang === "es" ? "Total" : "Total"}</span><span className="tabular-nums">{fmt(inv.total)}</span></div>
            <div className="flex justify-between text-[#15803D]"><span>{lang === "es" ? "Pagado" : "Paid"}</span><span className="tabular-nums">{fmt(inv.amount_paid)}</span></div>
            <div className={`flex justify-between font-serif-display text-xl ${inv.balance > 0 ? "text-[#8B1C22]" : "text-[#15803D]"}`}><span>{lang === "es" ? "Saldo" : "Balance"}</span><span className="tabular-nums">{fmt(inv.balance)}</span></div>
          </div>

          {inv.notes && <div className="mt-6 border-t border-[#E5E5E5] pt-4 text-sm text-[#404040]"><b>{lang === "es" ? "Notas" : "Notes"}:</b> {inv.notes}</div>}
        </div>

        <aside className="space-y-3">
          {inv.status !== "cancelled" && inv.balance > 0.005 && (
            <button onClick={() => setShowPay(true)} className="rn3-btn-primary w-full justify-center" data-testid="record-payment-btn">
              <DollarSign className="w-4 h-4" /> {lang === "es" ? "Registrar Pago" : "Record Payment"}
            </button>
          )}
          <button onClick={() => setShowSend(true)} className="rn3-btn-gold w-full justify-center" data-testid="email-invoice-btn">
            <Mail className="w-4 h-4" /> {lang === "es" ? "Enviar por Email" : "Email to Customer"}
          </button>
          <a
            href={`#`}
            onClick={async (e) => {
              e.preventDefault();
              try {
                const { data } = await api.get(`/back-office/books/${bookId}/invoices/${invoiceId}/pdf`, { responseType: "blob" });
                const url = window.URL.createObjectURL(data);
                const a = document.createElement("a"); a.href = url; a.download = `invoice-${inv.invoice_number || invoiceId}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => window.URL.revokeObjectURL(url), 4000);
              } catch { toast.error("Could not download PDF"); }
            }}
            className="rn3-btn-gold w-full justify-center"
            data-testid="download-invoice-pdf-btn"
          >
            <Download className="w-4 h-4" /> {lang === "es" ? "Descargar PDF" : "Download PDF"}
          </a>
          {inv.status === "draft" && (
            <button onClick={() => setStatus("sent")} className="rn3-btn-gold w-full justify-center" data-testid="mark-sent-btn">
              <Send className="w-4 h-4" /> {lang === "es" ? "Marcar como Enviada" : "Mark as Sent"}
            </button>
          )}
          {!["paid", "cancelled"].includes(inv.status) && inv.amount_paid === 0 && (
            <button onClick={() => setShowEdit(true)} className="rn3-btn-gold w-full justify-center" data-testid="edit-invoice-btn">
              <Edit3 className="w-4 h-4" /> {lang === "es" ? "Editar" : "Edit Invoice"}
            </button>
          )}
          {inv.status !== "cancelled" && inv.status !== "paid" && (
            <button onClick={() => setStatus("cancelled")} className="text-xs text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 w-full justify-center py-2" data-testid="cancel-invoice-btn">
              <Ban className="w-3 h-3" /> {lang === "es" ? "Cancelar" : "Cancel Invoice"}
            </button>
          )}
          {inv.amount_paid === 0 && (
            <button onClick={removeInvoice} className="text-xs text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 w-full justify-center py-2" data-testid="delete-invoice-btn">
              <Trash2 className="w-3 h-3" /> {lang === "es" ? "Eliminar" : "Delete"}
            </button>
          )}

          {(inv.payments || []).length > 0 && (
            <div className="border border-[#E5E5E5] p-4">
              <div className="rn3-eyebrow mb-3">{lang === "es" ? "Historial de Pagos" : "Payment History"}</div>
              <div className="space-y-2">
                {inv.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border-b border-[#F3EFE9] pb-2" data-testid={`pay-${p.id}`}>
                    <div>
                      <div className="text-[#0A0A0A]">{fmt(p.amount)} <span className="text-xs text-[#737373] uppercase ml-2">{p.method}</span></div>
                      <div className="text-xs text-[#737373]">{p.date}{p.reference ? ` · ${p.reference}` : ""}</div>
                    </div>
                    <button onClick={() => voidPayment(p.id)} className="text-[#737373] hover:text-[#8B1C22]" title="Void"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>

      <Modal open={showPay} onClose={() => setShowPay(false)} title={lang === "es" ? "Registrar Pago" : "Record Payment"} testid="pay-modal">
        <PaymentForm bookId={bookId} invoice={{ ...inv, customer_name: c.name }} accounts={accounts} lang={lang} onCancel={() => setShowPay(false)} onSaved={() => { setShowPay(false); load(); onChanged?.(); }} />
      </Modal>
      <Modal open={showEdit} onClose={() => setShowEdit(false)} title={lang === "es" ? "Editar Factura" : "Edit Invoice"} testid="edit-inv-modal" wide>
        <InvoiceForm bookId={bookId} customers={customers} incomeAccounts={incomeAccounts} lang={lang} existing={inv} onCancel={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); onChanged?.(); }} />
      </Modal>
      <SendDocModal open={showSend} onClose={() => setShowSend(false)} kind="invoice" bookId={bookId} doc={inv} defaultEmail={c.email} lang={lang} onSent={() => { load(); onChanged?.(); }} />
    </div>
  );
};

/* A/R Aging panel */
const ARAgingPanel = ({ bookId, lang, onCustomerClick }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/back-office/books/${bookId}/reports/ar-aging`).then(({ data }) => setData(data)).catch(() => {}); }, [bookId]);
  if (!data) return null;
  return (
    <div className="rn3-card" data-testid="ar-aging-panel">
      <div className="rn3-eyebrow">{lang === "es" ? "Antigüedad de Cuentas por Cobrar" : "A/R Aging"}</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
        {[["current","Current","Al día"],["1_30","1–30","1–30"],["31_60","31–60","31–60"],["61_90","61–90","61–90"],["over_90","90+","90+"]].map(([k, e, es]) => (
          <div key={k} className="border border-[#E5E5E5] p-3" data-testid={`ar-bucket-${k}`}>
            <div className={`text-xs uppercase tracking-widest ${k === "over_90" ? "text-[#8B1C22]" : k === "61_90" ? "text-[#C4A252]" : "text-[#737373]"}`}>{lang === "es" ? es : e}</div>
            <div className="font-serif-display text-2xl mt-1 tabular-nums">{fmt(data.buckets[k])}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-right text-sm">{lang === "es" ? "Total Pendiente" : "Total Outstanding"}: <b className="text-[#0A0A0A]">{fmt(data.total)}</b></div>
      {data.customers.length > 0 && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-widest text-[#737373] mb-2">{lang === "es" ? "Por Cliente" : "By Customer"}</div>
          <div className="space-y-1.5">
            {data.customers.map((c) => (
              <button key={c.customer_id} onClick={() => onCustomerClick(c.customer_id)} className="w-full flex items-center justify-between text-sm border border-[#E5E5E5] px-3 py-2 hover:bg-[#FDFBF7]" data-testid={`ar-cust-${c.customer_id}`}>
                <span>{c.customer_name}{c.customer_company ? ` · ${c.customer_company}` : ""}</span>
                <span className="font-serif-display text-lg tabular-nums">{fmt(c.total)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* Estimates sub-view */
const EstimatesView = ({ bookId, customers, incomeAccounts, lang, onConverted }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [sendEst, setSendEst] = useState(null);

  const ESTSTATUS = {
    draft:     { en: "Draft",     es: "Borrador",  cls: "border-[#737373] text-[#737373]" },
    sent:      { en: "Sent",      es: "Enviada",   cls: "border-[#C4A252] text-[#8B1C22]" },
    accepted:  { en: "Accepted",  es: "Aceptada",  cls: "border-[#15803D] text-[#15803D]" },
    declined:  { en: "Declined",  es: "Rechazada", cls: "border-[#8B1C22] text-[#8B1C22]" },
    expired:   { en: "Expired",   es: "Expirada",  cls: "border-[#737373] text-[#737373]" },
    converted: { en: "Converted", es: "Convertida",cls: "border-[#0A0A0A] text-[#0A0A0A]" },
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/back-office/books/${bookId}/estimates`); setItems(data); }
    catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookId]);

  const setStatus = async (id, status) => {
    await api.patch(`/back-office/books/${bookId}/estimates/${id}`, { status });
    load();
  };

  const convert = async (e) => {
    if (!window.confirm(`Convert ${e.estimate_number} to an invoice?`)) return;
    try {
      const { data } = await api.post(`/back-office/books/${bookId}/estimates/${e.id}/convert`);
      toast.success(`Created ${data.invoice_number}`);
      load();
      onConverted?.(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const del = async (e) => {
    if (!window.confirm("Delete this estimate?")) return;
    try { await api.delete(`/back-office/books/${bookId}/estimates/${e.id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const createEstimate = async (form) => {
    try {
      await api.post(`/back-office/books/${bookId}/estimates`, {
        customer_id: form.customer_id, date: form.date, items: form.items,
        tax_rate: Number(form.tax_rate || 0), notes: form.notes,
      });
      toast.success("Estimate created");
      setShowNew(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); throw err; }
  };

  return (
    <div className="space-y-4" data-testid="estimates-view">
      <div className="flex justify-end">
        <button onClick={() => { if (!customers.length) { toast.error("Add a customer first"); return; } setShowNew(true); }} className="rn3-btn-primary" data-testid="new-estimate-btn">
          <Plus className="w-4 h-4" /> {lang === "es" ? "Nuevo Presupuesto" : "New Estimate"}
        </button>
      </div>
      <div className="border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]" data-testid="estimates-table">
          <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
            <tr>
              <th className="py-3 px-3">#</th>
              <th className="py-3 px-3">{lang === "es" ? "Cliente" : "Customer"}</th>
              <th className="py-3 px-3">{lang === "es" ? "Fecha" : "Date"}</th>
              <th className="py-3 px-3">{lang === "es" ? "Expira" : "Expires"}</th>
              <th className="py-3 px-3 text-right">{lang === "es" ? "Total" : "Total"}</th>
              <th className="py-3 px-3">{lang === "es" ? "Estado" : "Status"}</th>
              <th className="py-3 px-3 text-right">{lang === "es" ? "Acciones" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="py-8 text-center text-[#737373]">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="7" className="py-12 text-center text-[#737373]">{lang === "es" ? "Sin presupuestos." : "No estimates yet."}</td></tr>}
            {items.map((e) => {
              const pill = ESTSTATUS[e.status] || ESTSTATUS.draft;
              return (
                <tr key={e.id} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7]" data-testid={`est-row-${e.id}`}>
                  <td className="py-2 px-3 font-mono text-[#8B1C22]">{e.estimate_number}</td>
                  <td className="py-2 px-3"><div>{e.customer_name}</div>{e.customer_company && <div className="text-xs text-[#737373]">{e.customer_company}</div>}</td>
                  <td className="py-2 px-3 whitespace-nowrap">{e.date}</td>
                  <td className="py-2 px-3 whitespace-nowrap">{e.expiration_date}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmt(e.total)}</td>
                  <td className="py-2 px-3">
                    <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} disabled={e.status === "converted"} className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${pill.cls}`} data-testid={`est-status-${e.id}`}>
                      {Object.entries(ESTSTATUS).map(([k, v]) => <option key={k} value={k}>{lang === "es" ? v.es : v.en}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-3 text-right text-xs space-x-3">
                    <button
                      onClick={async () => {
                        try {
                          const { data } = await api.get(`/back-office/books/${bookId}/estimates/${e.id}/pdf`, { responseType: "blob" });
                          const url = window.URL.createObjectURL(data);
                          const a = document.createElement("a"); a.href = url; a.download = `estimate-${e.estimate_number || e.id}.pdf`;
                          document.body.appendChild(a); a.click(); a.remove();
                          setTimeout(() => window.URL.revokeObjectURL(url), 4000);
                        } catch { toast.error("PDF failed"); }
                      }}
                      className="text-[#737373] hover:text-[#0A0A0A] inline-flex items-center gap-1"
                      data-testid={`est-pdf-${e.id}`}
                      title={lang === "es" ? "Descargar PDF" : "Download PDF"}
                    ><Download className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setSendEst(e)} className="text-[#C4A252] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid={`est-email-${e.id}`} title={lang === "es" ? "Enviar por email" : "Email to customer"}><Mail className="w-3.5 h-3.5" /></button>
                    {e.status !== "converted" && (
                      <button onClick={() => convert(e)} className="text-[#8B1C22] hover:underline inline-flex items-center gap-1" data-testid={`est-convert-${e.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> {lang === "es" ? "Convertir" : "Convert"}
                      </button>
                    )}
                    {e.status !== "converted" && (
                      <button onClick={() => del(e)} className="text-[#737373] hover:text-[#8B1C22]" data-testid={`est-del-${e.id}`}><Trash2 className="w-3.5 h-3.5 inline" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={showNew} onClose={() => setShowNew(false)} title={lang === "es" ? "Nuevo Presupuesto" : "New Estimate"} testid="new-est-modal" wide>
        <InvoiceForm bookId={bookId} customers={customers} incomeAccounts={incomeAccounts} lang={lang} mode="estimate" onCancel={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />
      </Modal>
      <SendDocModal open={!!sendEst} onClose={() => setSendEst(null)} kind="estimate" bookId={bookId} doc={sendEst} defaultEmail={customers.find((c) => c.id === sendEst?.customer_id)?.email} lang={lang} onSent={() => load()} />
    </div>
  );
};

/* Main InvoicesTab */
const InvoicesTab = ({ book, accounts, lang }) => {
  const [view, setView] = useState("invoices"); // invoices | customers
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewInv, setShowNewInv] = useState(false);
  const [showNewCust, setShowNewCust] = useState(false);
  const [editCust, setEditCust] = useState(null);
  const [stmtCust, setStmtCust] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(null); // null | "customer" | "reminder"
  const [activeInv, setActiveInv] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [q, setQ] = useState("");

  const incomeAccounts = useMemo(() => accounts.filter((a) => a.type === "income" && !a.is_header && a.is_active), [accounts]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: invs }, { data: cs }] = await Promise.all([
        api.get(`/back-office/books/${book.id}/invoices`),
        api.get(`/back-office/books/${book.id}/customers`),
      ]);
      setInvoices(invs); setCustomers(cs);
    } catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [book.id]);

  const filteredInvs = useMemo(() => invoices.filter((i) => {
    if (filterStatus !== "all" && i.status !== filterStatus) return false;
    if (filterCustomer && i.customer_id !== filterCustomer) return false;
    if (q) {
      const lq = q.toLowerCase();
      if (!(i.invoice_number || "").toLowerCase().includes(lq) &&
          !(i.customer_name || "").toLowerCase().includes(lq) &&
          !(i.customer_company || "").toLowerCase().includes(lq)) return false;
    }
    return true;
  }), [invoices, filterStatus, filterCustomer, q]);

  if (activeInv) {
    return <InvoiceDetail
      bookId={book.id} invoiceId={activeInv} accounts={accounts} lang={lang}
      onBack={() => { setActiveInv(null); refresh(); }}
      onChanged={refresh}
    />;
  }

  return (
    <div className="space-y-6" data-testid="invoices-tab">
      {/* Sub-tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1">
          <button onClick={() => setView("invoices")} className={`px-4 py-2 text-xs uppercase tracking-widest border ${view === "invoices" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`} data-testid="sub-invoices">
            <FileText className="w-3.5 h-3.5 inline mr-1" /> {lang === "es" ? "Facturas" : "Invoices"}
          </button>
          <button onClick={() => setView("customers")} className={`px-4 py-2 text-xs uppercase tracking-widest border ${view === "customers" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`} data-testid="sub-customers">
            <Users className="w-3.5 h-3.5 inline mr-1" /> {lang === "es" ? "Clientes" : "Customers"} ({customers.length})
          </button>
          <button onClick={() => setView("estimates")} className={`px-4 py-2 text-xs uppercase tracking-widest border ${view === "estimates" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`} data-testid="sub-estimates">
            <FileText className="w-3.5 h-3.5 inline mr-1" /> {lang === "es" ? "Presupuestos" : "Estimates"}
          </button>
        </div>
      {view === "invoices" && (
          <div className="flex items-center gap-2">
            <button onClick={() => setBulkOpen("reminder")} className="rn3-btn-gold" data-testid="bulk-invoice-reminders-btn">
              <Send className="w-4 h-4" /> {lang === "es" ? "Recordatorios" : "Send Reminders"}
            </button>
            <button onClick={() => { if (!customers.length) { toast.error("Add a customer first"); setView("customers"); return; } setShowNewInv(true); }} className="rn3-btn-primary" data-testid="new-invoice-btn">
              <Plus className="w-4 h-4" /> {lang === "es" ? "Nueva Factura" : "New Invoice"}
            </button>
          </div>
        )}
        {view === "customers" && (
          <div className="flex items-center gap-2">
            <button onClick={() => setBulkOpen("customer")} className="rn3-btn-gold" data-testid="bulk-customer-stmts-btn">
              <Send className="w-4 h-4" /> {lang === "es" ? "Envío Masivo" : "Bulk Statements"}
            </button>
            <button onClick={() => setShowNewCust(true)} className="rn3-btn-primary" data-testid="new-customer-btn">
              <Plus className="w-4 h-4" /> {lang === "es" ? "Nuevo Cliente" : "New Customer"}
            </button>
          </div>
        )}
      </div>

      {view === "estimates" && (
        <EstimatesView bookId={book.id} customers={customers} incomeAccounts={incomeAccounts} lang={lang} onConverted={(id) => { setView("invoices"); refresh(); setActiveInv(id); }} />
      )}

      {view === "invoices" && (
        <>
          <ARAgingPanel bookId={book.id} lang={lang} onCustomerClick={(cid) => { setFilterCustomer(cid); setFilterStatus("all"); }} />

          <div className="flex flex-wrap items-center gap-3" data-testid="inv-filters">
            <div className="flex items-center gap-2 px-3 py-2 border border-[#E5E5E5]">
              <Search className="w-3.5 h-3.5 text-[#737373]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "es" ? "Buscar…" : "Search…"} className="bg-transparent outline-none text-sm w-56" data-testid="inv-search" />
            </div>
            <select value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)} className="border border-[#E5E5E5] px-3 py-2 text-sm" data-testid="inv-customer-filter">
              <option value="">{lang === "es" ? "Todos los Clientes" : "All Customers"}</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="flex gap-1">
              {[["all", lang==="es"?"Todas":"All"], ...Object.entries(STATUS_PILLS).map(([k, v]) => [k, lang==="es"?v.es:v.en])].map(([k, label]) => (
                <button key={k} onClick={() => setFilterStatus(k)} data-testid={`inv-filter-${k}`}
                  className={`px-3 py-2 text-xs uppercase tracking-wider border ${filterStatus===k?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"border-[#E5E5E5] hover:border-[#C4A252]"}`}>{label}</button>
              ))}
            </div>
          </div>

          <div className="border border-[#E5E5E5] overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]" data-testid="invoices-table">
              <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
                <tr>
                  <th className="py-3 px-3">#</th>
                  <th className="py-3 px-3">{lang === "es" ? "Cliente" : "Customer"}</th>
                  <th className="py-3 px-3">{lang === "es" ? "Fecha" : "Date"}</th>
                  <th className="py-3 px-3">{lang === "es" ? "Vence" : "Due"}</th>
                  <th className="py-3 px-3 text-right">{lang === "es" ? "Total" : "Total"}</th>
                  <th className="py-3 px-3 text-right">{lang === "es" ? "Saldo" : "Balance"}</th>
                  <th className="py-3 px-3">{lang === "es" ? "Estado" : "Status"}</th>
                  <th className="py-3 px-3 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="8" className="py-8 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>}
                {!loading && filteredInvs.length === 0 && (
                  <tr><td colSpan="8" className="py-12 text-center text-[#737373]">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-[#C4A252]" />
                    {lang === "es" ? "Sin facturas." : "No invoices yet. Create one to get started."}
                  </td></tr>
                )}
                {filteredInvs.map((i) => {
                  const pill = STATUS_PILLS[i.status] || STATUS_PILLS.draft;
                  return (
                    <tr key={i.id} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7] cursor-pointer" onClick={() => setActiveInv(i.id)} data-testid={`inv-row-${i.id}`}>
                      <td className="py-2 px-3 font-mono text-[#8B1C22]">{i.invoice_number}</td>
                      <td className="py-2 px-3"><div>{i.customer_name}</div>{i.customer_company && <div className="text-xs text-[#737373]">{i.customer_company}</div>}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{i.date}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{i.due_date}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmt(i.total)}</td>
                      <td className={`py-2 px-3 text-right tabular-nums ${i.balance > 0 ? "text-[#8B1C22]" : "text-[#15803D]"}`}>{fmt(i.balance)}</td>
                      <td className="py-2 px-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${pill.cls}`}>{lang === "es" ? pill.es : pill.en}</span></td>
                      <td className="py-2 px-3"><ChevronRight className="w-4 h-4 text-[#737373]" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === "customers" && (
        <div className="border border-[#E5E5E5]">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
              <tr>
                <th className="py-3 px-3">{lang === "es" ? "Nombre" : "Name"}</th>
                <th className="py-3 px-3">{lang === "es" ? "Empresa" : "Company"}</th>
                <th className="py-3 px-3">{lang === "es" ? "Contacto" : "Contact"}</th>
                <th className="py-3 px-3">{lang === "es" ? "Términos" : "Terms"}</th>
                <th className="py-3 px-3 text-right">{lang === "es" ? "Saldo Abierto" : "Open Balance"}</th>
                <th className="py-3 px-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 && (
                <tr><td colSpan="6" className="py-12 text-center text-[#737373]">
                  <Users className="w-8 h-8 mx-auto mb-2 text-[#C4A252]" />
                  {lang === "es" ? "Sin clientes aún." : "No customers yet. Add one to start invoicing."}
                </td></tr>
              )}
              {customers.map((c) => (
                <tr key={c.id} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7]" data-testid={`cust-row-${c.id}`}>
                  <td className="py-2 px-3 font-medium">{c.name}</td>
                  <td className="py-2 px-3 text-[#737373]">{c.company || ""}</td>
                  <td className="py-2 px-3 text-xs text-[#737373]">{c.email}{c.email && c.phone ? " · " : ""}{c.phone}</td>
                  <td className="py-2 px-3 text-xs uppercase tracking-widest">{TERMS.find(t=>t.key===c.default_terms)?.[lang]||c.default_terms}</td>
                  <td className={`py-2 px-3 text-right tabular-nums ${c.open_balance > 0 ? "text-[#8B1C22]" : ""}`}>{fmt(c.open_balance || 0)}</td>
                  <td className="py-2 px-3 text-right space-x-3">
                    <button onClick={() => setStmtCust(c)} className="text-[#737373] hover:text-[#0A0A0A] inline-flex items-center gap-1" data-testid={`cust-stmt-${c.id}`} title={lang === "es" ? "Estado de cuenta" : "Statement"}><FileDown className="w-4 h-4" /></button>
                    <button onClick={() => setEditCust(c)} className="text-[#737373] hover:text-[#0A0A0A]" data-testid={`cust-edit-${c.id}`}><Edit3 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showNewInv} onClose={() => setShowNewInv(false)} title={lang === "es" ? "Nueva Factura" : "New Invoice"} testid="new-inv-modal" wide>
        <InvoiceForm bookId={book.id} customers={customers} incomeAccounts={incomeAccounts} lang={lang} onCancel={() => setShowNewInv(false)} onSaved={(d) => { setShowNewInv(false); refresh(); setActiveInv(d.id); }} />
      </Modal>
      <Modal open={showNewCust} onClose={() => setShowNewCust(false)} title={lang === "es" ? "Nuevo Cliente" : "New Customer"} testid="new-cust-modal">
        <CustomerForm bookId={book.id} lang={lang} onCancel={() => setShowNewCust(false)} onSaved={() => { setShowNewCust(false); refresh(); }} />
      </Modal>
      <Modal open={!!editCust} onClose={() => setEditCust(null)} title={lang === "es" ? "Editar Cliente" : "Edit Customer"} testid="edit-cust-modal">
        {editCust && <CustomerForm bookId={book.id} lang={lang} existing={editCust} onCancel={() => setEditCust(null)} onSaved={() => { setEditCust(null); refresh(); }} />}
      </Modal>
      <StatementModal open={!!stmtCust} onClose={() => setStmtCust(null)} kind="customer" bookId={book.id} party={stmtCust} lang={lang} />
      <BulkSendModal open={!!bulkOpen} onClose={() => setBulkOpen(null)} bookId={book.id} kind={bulkOpen || "customer"} lang={lang} onSent={() => refresh()} />
    </div>
  );
};

export default InvoicesTab;
